import { Marker, Polyline, useGoogleMap } from '@react-google-maps/api';
import React, { Dispatch, SetStateAction, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { LatLngLiteral } from '@/components/Map/types';
import theme from '@/main/theme';

interface Props {
  /** When true, map clicks add polygon vertices. */
  active: boolean;
  points: LatLngLiteral[];
  setPoints: Dispatch<SetStateAction<LatLngLiteral[]>>;
  /** Called with the finished vertices when the user double-clicks to close. */
  onComplete: (points: LatLngLiteral[], map: google.maps.Map) => void;
}

/**
 * Manual polygon drawing for Google Maps. Replaces the removed DrawingManager
 * (`google.maps.drawing`, gone since Maps JS API v3.65): map clicks add
 * vertices, each vertex can be dragged, and clicking the first one closes the
 * polygon once there are at least three. Must be rendered inside a
 * <GoogleMap> so `useGoogleMap` can attach listeners to the map instance.
 */
function PolygonDraw({
  active,
  points,
  setPoints,
  onComplete,
}: Props): React.ReactElement | null {
  const map = useGoogleMap();
  const { t } = useTranslation();

  // Set while a click that landed on the map itself is still fresh, so a
  // following double-click can tell whether the vertex under the cursor was
  // created by that double-click's own first click, or was already there.
  const justAddedRef = useRef(false);

  useEffect(() => {
    if (!map || !active) {
      return;
    }

    const clickListener = map.addListener(
      'click',
      (e: google.maps.MapMouseEvent) => {
        if (!e.latLng) {
          return;
        }
        const point = { lat: e.latLng.lat(), lng: e.latLng.lng() };
        setPoints(prev => [...prev, point]);
        justAddedRef.current = true;
        window.setTimeout(() => {
          justAddedRef.current = false;
        }, 400);
      },
    );

    // Google always fires one `click` before `dblclick`, so a double-click
    // leaves a stray vertex behind. Drop it again and let the native zoom
    // run. This only fires when the second click misses the vertex the
    // first one created; otherwise the marker below handles it.
    const dblClickListener = map.addListener('dblclick', () => {
      if (!justAddedRef.current) {
        return;
      }
      justAddedRef.current = false;
      setPoints(prev => prev.slice(0, -1));
    });

    return () => {
      clickListener.remove();
      dblClickListener.remove();
    };
  }, [map, active, setPoints]);

  // Discard any in-progress polygon when drawing mode is turned off.
  useEffect(() => {
    if (!active) {
      setPoints([]);
    }
  }, [active, setPoints]);

  if (!active || points.length === 0) {
    return null;
  }

  const canClose = points.length >= 3;

  return (
    <>
      <Polyline
        path={points}
        options={{
          clickable: false,
          strokeColor: theme.colors.primaryPurple,
          strokeOpacity: 1,
          strokeWeight: 2,
          zIndex: 2,
        }}
      />

      {/* Own vertex handles rather than the Polyline's `editable`, which
          always draws midpoint handles that insert new vertices - the Maps
          API has no way to turn those off. These also write the move back
          into `points`, which `editable` does not: it mutates Google's own
          path while React keeps re-rendering from state. */}
      {points.map((point, index) => (
        <Marker
          key={index}
          position={point}
          draggable
          zIndex={3}
          cursor={index === 0 && canClose ? 'pointer' : 'move'}
          title={
            index === 0 && canClose
              ? t('Click the first point to close the area')
              : t('Drag to move the point')
          }
          icon={{
            path: window.google.maps.SymbolPath.CIRCLE,
            // The first vertex grows once the shape can close, to show it is the one to click.
            scale: index === 0 && canClose ? 8 : 5,
            fillColor: theme.colors.primaryPurple,
            fillOpacity: 1,
            strokeColor: '#fff',
            strokeWeight: 2,
          }}
          onDblClick={() => {
            // remove any point that was just added by the first click of this double-click
            if (justAddedRef.current) {
              justAddedRef.current = false;
              setPoints(prev => prev.slice(0, -1));
            }
            if (map) {
              map.setZoom((map.getZoom() ?? 0) + 1);
              map.panTo(point);
            }
          }}
          onClick={() => {
            if (index !== 0 || !canClose || !map) {
              return;
            }
            onComplete(points, map);
            setPoints([]);
          }}
          onDragEnd={event => {
            if (!event.latLng) {
              return;
            }
            const moved = {
              lat: event.latLng.lat(),
              lng: event.latLng.lng(),
            };
            setPoints(prev =>
              prev.map((existing, i) => (i === index ? moved : existing)),
            );
          }}
        />
      ))}
    </>
  );
}

export default PolygonDraw;
